import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import useExperiment from "../hooks/useExperiment";
import { industries as FULL_INDUSTRIES, countries, statesByCountry, citiesByState } from "../data/formOptions";
import {
  Zap,
  Search,
  MapPin,
  Clock,
  Building2,
  DollarSign,
  Filter,
  X,
  Briefcase,
  Award,
  Flame,
  ChevronDown,
  Home,
  Target,
  AlertTriangle,
  Bookmark,
  Loader2,
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Link2,
  CheckCircle,
  MessageCircle
} from "lucide-react";

const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-Time" },
  { value: "part-time", label: "Part-Time" },
  { value: "contract", label: "Contract" },
  { value: "freelance", label: "Freelance" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" }
];

const SENIORITY_LEVELS = [
  { value: "entry", label: "Entry Level" },
  { value: "mid", label: "Mid Level" },
  { value: "senior", label: "Senior Level" },
  { value: "lead", label: "Lead / Manager" },
  { value: "executive", label: "Executive" }
];

const JobsBrowser = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [matchScores, setMatchScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showLowMatchWarning, setShowLowMatchWarning] = useState(null);
  
  // Location filter states
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  
  // Saved jobs state
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const [savingJob, setSavingJob] = useState(null);
  
  // Share state
  const [shareMenuJob, setShareMenuJob] = useState(null);
  const [copiedJobLink, setCopiedJobLink] = useState(null);
  
  // A/B Testing: Job card layout experiment
  const { 
    variant: layoutVariant, 
    config: layoutConfig, 
    trackConversion: trackLayoutConversion,
    isInExperiment: isInLayoutExperiment 
  } = useExperiment("job_card_layout");
  
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    industry: searchParams.get("industry") || "",
    location: searchParams.get("location") || "",
    employment_type: searchParams.get("employment_type") || "",
    seniority_level: searchParams.get("seniority_level") || "",
    skills: searchParams.get("skills") || "",
    job_title: searchParams.get("job_title") || "",
    job_function: searchParams.get("job_function") || "",
    is_urgent: searchParams.get("is_urgent") === "true"
  });

  const [browseIndustries, setBrowseIndustries] = useState([]);
  const [browseCompanies, setBrowseCompanies] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchBrowseData();
    if (user?.role === "jobseeker") {
      fetchMatchScores();
      fetchSavedJobs();
    }
  }, [searchParams, user]);

  const fetchBrowseData = async () => {
    try {
      const [indRes, compRes] = await Promise.all([
        fetch(`${API}/jobs/browse/industries`),
        fetch(`${API}/jobs/browse/companies`)
      ]);
      if (indRes.ok) setBrowseIndustries(await indRes.json());
      if (compRes.ok) setBrowseCompanies(await compRes.json());
    } catch {}
  };
  
  const fetchSavedJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/saved/list`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSavedJobIds(new Set(data.map(j => j.job_id)));
      }
    } catch (error) {
      console.error("Failed to fetch saved jobs:", error);
    }
  };
  
  const handleSaveJob = async (jobId, jobTitle) => {
    if (!user) {
      toast.error("Please login to save jobs");
      return;
    }
    
    setSavingJob(jobId);
    const isSaved = savedJobIds.has(jobId);
    
    try {
      const response = await fetch(`${API}/jobs/${jobId}/save`, {
        method: isSaved ? "DELETE" : "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        if (isSaved) {
          setSavedJobIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(jobId);
            return newSet;
          });
          toast.success(`Removed from saved jobs`);
        } else {
          setSavedJobIds(prev => new Set([...prev, jobId]));
          toast.success(`Saved "${jobTitle}"`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save job");
      }
    } catch (error) {
      toast.error("Failed to save job");
    } finally {
      setSavingJob(null);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.industry) params.append("industry", filters.industry);
      if (filters.location) params.append("location", filters.location);
      if (filters.employment_type) params.append("employment_type", filters.employment_type);
      if (filters.seniority_level) params.append("seniority_level", filters.seniority_level);
      if (filters.skills) params.append("skills", filters.skills);
      if (filters.job_title) params.append("title", filters.job_title);

      const response = await fetch(`${API}/jobs?${params.toString()}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Handle paginated response
        if (data.jobs && data.pagination) {
          setJobs(data.jobs);
        } else if (Array.isArray(data)) {
          setJobs(data);
        } else {
          setJobs(data.jobs || []);
        }
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchScores = async () => {
    try {
      const response = await fetch(`${API}/matching/jobs?limit=100`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Create a map of job_id to match score
        const scores = {};
        data.forEach(item => {
          scores[item.job.job_id] = {
            score: item.match_score,
            details: item.match_details
          };
        });
        setMatchScores(scores);
      }
    } catch (error) {
      console.error("Failed to fetch match scores:", error);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      industry: "",
      location: "",
      employment_type: "",
      seniority_level: "",
      skills: "",
      job_title: "",
      job_function: "",
      is_urgent: false
    });
    setSearchParams({});
  };

  const handleBrowseIndustry = (industry) => {
    const params = new URLSearchParams(searchParams);
    params.set("industry", industry);
    setSearchParams(params);
  };

  const handleBrowseCompany = (company) => {
    const params = new URLSearchParams(searchParams);
    params.set("search", company);
    setSearchParams(params);
  };

  // Social sharing functions
  const getJobUrl = (jobId) => `${window.location.origin}/jobs/${jobId}`;

  const getJobShareText = (job) => {
    // Build location string (Country, State)
    const locationParts = [];
    if (job.location) {
      const parts = job.location.split(', ');
      if (parts.length >= 2) {
        locationParts.push(parts.slice(-2).join(', '));
      } else {
        locationParts.push(job.location);
      }
    }
    
    // Build work hours string
    let workHours = '';
    if (job.work_timing_start && job.work_timing_end) {
      workHours = ` | ${job.work_timing_start} - ${job.work_timing_end}`;
    } else if (job.shift_type) {
      const shiftLabels = {
        first: 'Morning Shift',
        second: 'Afternoon Shift',
        third: 'Night Shift',
        rotating: 'Rotating Shifts',
        flexible: 'Flexible Hours'
      };
      workHours = ` | ${shiftLabels[job.shift_type] || job.shift_type}`;
    }
    
    const companyName = job.company_name || 'Company';
    const location = locationParts.length > 0 ? ` in ${locationParts[0]}` : '';
    
    return `${job.title} at ${companyName}${location}${workHours}. Apply now!`;
  };

  const shareJobToTwitter = (job) => {
    const text = encodeURIComponent(getJobShareText(job));
    const url = encodeURIComponent(getJobUrl(job.job_id));
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
    setShareMenuJob(null);
  };

  const shareJobToLinkedIn = (job) => {
    const url = encodeURIComponent(getJobUrl(job.job_id));
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=600');
    setShareMenuJob(null);
  };

  const shareJobToFacebook = (job) => {
    const url = encodeURIComponent(getJobUrl(job.job_id));
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
    setShareMenuJob(null);
  };

  const copyJobLink = async (jobId) => {
    try {
      await navigator.clipboard.writeText(getJobUrl(jobId));
      setCopiedJobLink(jobId);
      toast.success("Link copied!");
      setTimeout(() => setCopiedJobLink(null), 2000);
    } catch (err) {
      toast.error("Failed to copy link");
    }
    setShareMenuJob(null);
  };

  const shareJobToWhatsApp = (job) => {
    const text = encodeURIComponent(`${getJobShareText(job)}\n\n${getJobUrl(job.job_id)}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    setShareMenuJob(null);
  };

  const handleApply = async (jobId) => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL - Use custom Google OAuth
      try {
        const response = await fetch(`${API}/auth/google/url`, {
          credentials: "include",
          headers: { "Origin": window.location.origin }
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.url;
          return;
        }
      } catch (error) {
        console.error("Failed to get Google OAuth URL");
      }
      // Fallback
      const redirectUrl = window.location.origin + "/auth/callback";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    // Check match score and warn if below 50%
    const matchData = matchScores[jobId];
    if (matchData && matchData.score < 50 && !showLowMatchWarning) {
      setShowLowMatchWarning(jobId);
      return;
    }

    try {
      const response = await fetch(`${API}/jobs/${jobId}/apply`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Application submitted!");
        setShowLowMatchWarning(null);
        fetchJobs();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to apply");
      }
    } catch (error) {
      toast.error("Failed to apply");
    }
  };

  const getMatchColor = (score) => {
    if (score >= 80) return "text-green-400 bg-green-500/20 border-green-500/30";
    if (score >= 60) return "text-blue-400 bg-blue-500/20 border-blue-500/30";
    if (score >= 50) return "text-yellow-400 bg-yellow-500/20 border-yellow-500/30";
    return "text-orange-400 bg-orange-500/20 border-orange-500/30";
  };

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              BROWSE <span className="text-crimson">JOBS</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">Find your next opportunity</p>
          </div>

          {/* Search Bar */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                  placeholder="Search jobs..."
                  className="bg-obsidian-light border-[#333] text-white placeholder:text-gray-600 pl-12 h-12 sm:h-14 text-base sm:text-lg"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2 sm:gap-4">
                <Button
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outline"
                  className={`flex-1 sm:flex-none border-[#333] text-white h-12 sm:h-14 px-4 sm:px-6 ${showFilters ? "border-crimson" : ""}`}
                  data-testid="filter-toggle"
                >
                  <Filter className="w-5 h-5 sm:mr-2" />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 w-5 h-5 bg-crimson rounded-full text-xs flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
                <Button
                  onClick={applyFilters}
                  className="flex-1 sm:flex-none bg-crimson hover:bg-crimson-hover text-white h-12 sm:h-14 px-4 sm:px-8 uppercase tracking-wider text-sm"
                  data-testid="search-btn"
                >
                  Search
                </Button>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <motion.div 
                className="mt-4 p-6 bg-obsidian-light border border-[#222]"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Industry</label>
                    <select
                      value={filters.industry}
                      onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="">All Industries</option>
                      {FULL_INDUSTRIES.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Employment Type</label>
                    <select
                      value={filters.employment_type}
                      onChange={(e) => setFilters(prev => ({ ...prev, employment_type: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="">All Types</option>
                      {EMPLOYMENT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Seniority</label>
                    <select
                      value={filters.seniority_level}
                      onChange={(e) => setFilters(prev => ({ ...prev, seniority_level: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="">All Levels</option>
                      {SENIORITY_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Location Dropdowns */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Country</label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => {
                        const country = e.target.value;
                        setSelectedCountry(country);
                        setSelectedState("");
                        setSelectedCity("");
                        setAvailableStates(statesByCountry[country] || []);
                        setAvailableCities([]);
                        // Update location filter
                        setFilters(prev => ({ ...prev, location: country }));
                      }}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                    >
                      <option value="">All Countries</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">State/Region</label>
                    <select
                      value={selectedState}
                      onChange={(e) => {
                        const state = e.target.value;
                        setSelectedState(state);
                        setSelectedCity("");
                        const cities = citiesByState[selectedCountry]?.[state] || [];
                        setAvailableCities(cities);
                        // Update location filter
                        setFilters(prev => ({ ...prev, location: state ? `${state}, ${selectedCountry}` : selectedCountry }));
                      }}
                      disabled={!selectedCountry || availableStates.length === 0}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white disabled:opacity-50"
                    >
                      <option value="">All States</option>
                      {availableStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">City</label>
                    <select
                      value={selectedCity}
                      onChange={(e) => {
                        const city = e.target.value;
                        setSelectedCity(city);
                        // Update location filter
                        if (city) {
                          setFilters(prev => ({ ...prev, location: `${city}, ${selectedState}` }));
                        } else if (selectedState) {
                          setFilters(prev => ({ ...prev, location: `${selectedState}, ${selectedCountry}` }));
                        }
                      }}
                      disabled={!selectedState || availableCities.length === 0}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white disabled:opacity-50"
                    >
                      <option value="">All Cities</option>
                      {availableCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Skills</label>
                    <Input
                      value={filters.skills}
                      onChange={(e) => setFilters(prev => ({ ...prev, skills: e.target.value }))}
                      placeholder="e.g., Python, React"
                      className="bg-obsidian border-[#333] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Job Title</label>
                    <Input
                      value={filters.job_title}
                      onChange={(e) => setFilters(prev => ({ ...prev, job_title: e.target.value }))}
                      placeholder="e.g., Developer, Manager"
                      className="bg-obsidian border-[#333] text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Job Function</label>
                    <select
                      value={filters.job_function}
                      onChange={(e) => setFilters(prev => ({ ...prev, job_function: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
                      data-testid="filter-job-function"
                    >
                      <option value="">All Functions</option>
                      {["Accounting & Finance", "Administration", "Customer Service", "Data & Analytics",
                        "Design & Creative", "Engineering", "Healthcare", "Human Resources",
                        "IT & Technology", "Legal", "Logistics & Supply Chain", "Marketing",
                        "Operations", "Product Management", "Project Management", "Quality Assurance",
                        "Research & Development", "Sales & Business Development", "Security",
                        "Strategy & Consulting", "Teaching & Education", "Writing & Content", "Other"
                      ].map(fn => (
                        <option key={fn} value={fn}>{fn}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 cursor-pointer h-10 px-3 bg-obsidian border border-[#333] w-full">
                      <input
                        type="checkbox"
                        checked={filters.is_urgent}
                        onChange={(e) => setFilters(prev => ({ ...prev, is_urgent: e.target.checked }))}
                        className="w-4 h-4 accent-crimson"
                        data-testid="filter-urgent"
                      />
                      <span className="text-sm text-amber-400 font-medium">Urgently Hiring Only</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                  <Button
                    onClick={clearFilters}
                    variant="ghost"
                    className="text-gray-400 hover:text-white"
                  >
                    Clear All
                  </Button>
                  <Button
                    onClick={applyFilters}
                    className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
                  >
                    Apply Filters
                  </Button>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Browse by Industry */}
          {browseIndustries.length > 0 && !searchParams.get("industry") && (
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              data-testid="browse-industries"
            >
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-crimson" />
                Browse by Industry
              </h3>
              <div className="flex flex-wrap gap-2">
                {browseIndustries.map((ind) => (
                  <button
                    key={ind.industry}
                    onClick={() => handleBrowseIndustry(ind.industry)}
                    className="px-4 py-2 bg-obsidian-light border border-[#222] hover:border-crimson/50 text-sm text-gray-300 hover:text-white transition-all group"
                    data-testid={`industry-bubble-${ind.industry}`}
                  >
                    {ind.industry}
                    <span className="ml-2 text-crimson group-hover:text-white text-xs font-bold">{ind.count}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Browse by Company */}
          {browseCompanies.length > 0 && !searchParams.get("search") && (
            <motion.div
              className="mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              data-testid="browse-companies"
            >
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-crimson" />
                Browse by Company
              </h3>
              <div className="flex flex-wrap gap-2">
                {browseCompanies.map((comp) => (
                  <button
                    key={comp.company}
                    onClick={() => handleBrowseCompany(comp.company)}
                    className="flex items-center gap-2 px-4 py-2 bg-obsidian-light border border-[#222] hover:border-crimson/50 text-sm text-gray-300 hover:text-white transition-all group"
                    data-testid={`company-bubble-${comp.company}`}
                  >
                    {comp.logo ? (
                      <img src={comp.logo} alt="" className="w-5 h-5 rounded object-cover" />
                    ) : (
                      <Building2 className="w-4 h-4 text-gray-600" />
                    )}
                    {comp.company}
                    <span className="text-crimson group-hover:text-white text-xs font-bold">{comp.count}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Active filter tag */}
          {(searchParams.get("industry") || searchParams.get("search")) && (
            <div className="mb-4 flex items-center gap-2">
              {searchParams.get("industry") && (
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-crimson/10 border border-crimson/30 text-crimson text-sm">
                  Industry: {searchParams.get("industry")}
                  <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete("industry"); setSearchParams(p); }} className="hover:text-white"><X className="w-3 h-3" /></button>
                </span>
              )}
            </div>
          )}

          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-400">
              <span className="text-white font-bold">{jobs.length}</span> jobs found
            </p>
          </div>

          {/* Job Listings */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : jobs.length > 0 ? (
            <div className="space-y-4">
              {jobs.map((job, index) => {
                // A/B Test: Determine if we should show match score based on experiment
                const showMatchScore = layoutConfig?.show_match_score !== false;
                const isCompactLayout = layoutConfig?.layout === "compact";
                
                return (
                <motion.div
                  key={job.job_id}
                  className={`bg-obsidian-light border ${isCompactLayout ? 'p-4' : 'p-6'} hover:border-crimson/50 transition-all relative ${
                    job.promoted ? "border-crimson/50" : "border-[#222]"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => {
                    // Track job click conversion for A/B test
                    if (isInLayoutExperiment) {
                      trackLayoutConversion("job_click", { job_id: job.job_id, variant: layoutVariant });
                    }
                  }}
                >
                  {job.promoted && (
                    <div className="absolute -top-2 -right-2 bg-crimson px-2 py-1 flex items-center gap-1">
                      <Flame className="w-3 h-3 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-bold">Promoted</span>
                    </div>
                  )}
                  {job.is_urgent && !job.promoted && (
                    <div className="absolute -top-2 -right-2 bg-amber-500 px-2 py-1 flex items-center gap-1" data-testid={`urgent-badge-${job.job_id}`}>
                      <AlertTriangle className="w-3 h-3 text-black" />
                      <span className="text-xs uppercase tracking-wider text-black font-bold">Urgently Hiring</span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className={`font-heading ${isCompactLayout ? 'text-base' : 'text-lg sm:text-xl'} font-bold break-words`}>{job.title}</h3>
                        {job.featured_employer && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50">
                            <Award className="w-3 h-3 text-yellow-500" />
                            <span className="text-xs text-yellow-500">Verified</span>
                          </span>
                        )}
                        {job.is_urgent && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 border border-amber-500/50">
                            <AlertTriangle className="w-3 h-3 text-amber-500" />
                            <span className="text-xs text-amber-500 font-bold">Urgent</span>
                          </span>
                        )}
                        {job.job_function && (
                          <span className="px-2 py-0.5 bg-white/5 border border-[#333] text-xs text-gray-400">{job.job_function}</span>
                        )}
                        {/* Match Score Badge - Controlled by A/B Test */}
                        {showMatchScore && user?.role === "jobseeker" && matchScores[job.job_id] && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 border ${getMatchColor(matchScores[job.job_id].score)}`}>
                            <Target className="w-3 h-3" />
                            <span className="text-xs font-bold">{matchScores[job.job_id].score}% Match</span>
                          </span>
                        )}
                      </div>
                      
                      {job.recruiter_name && (
                        <p className="text-gray-500 text-sm mb-2">by {job.recruiter_name}</p>
                      )}

                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400 mb-4">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{job.industry}</span>
                        </span>
                        {job.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{job.location}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{job.seniority_level}</span>
                        </span>
                        <span className="tag text-xs">{job.employment_type}</span>
                        {job.salary_min && job.salary_max && (
                          <span className="flex items-center gap-1 text-crimson">
                            <DollarSign className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs sm:text-sm">{job.salary_currency || "USD"} {job.salary_min.toLocaleString()} - {job.salary_max.toLocaleString()}</span>
                          </span>
                        )}
                      </div>

                      {job.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {job.skills.slice(0, 5).map((skill, idx) => (
                            <span key={idx} className="px-2 py-1 bg-[#1a1a1a] text-xs text-gray-300">
                              {skill}
                            </span>
                          ))}
                          {job.skills.length > 5 && (
                            <span className="text-gray-500 text-xs">+{job.skills.length - 5} more</span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex sm:flex-col gap-2 w-full sm:w-auto flex-shrink-0">
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                        variant="outline"
                        className="flex-1 sm:flex-none border-[#333] hover:border-crimson text-white text-xs sm:text-sm uppercase tracking-wider"
                        data-testid={`view-${job.job_id}`}
                      >
                        View Details
                      </Button>
                      {user?.role === "jobseeker" && (
                        <>
                          <Button
                            onClick={() => handleSaveJob(job.job_id, job.title)}
                            variant="outline"
                            className={`flex-1 sm:flex-none border-[#333] text-xs sm:text-sm ${
                              savedJobIds.has(job.job_id) 
                                ? 'border-crimson bg-crimson/10 text-crimson' 
                                : 'hover:border-crimson text-white'
                            }`}
                            disabled={savingJob === job.job_id}
                            data-testid={`save-${job.job_id}`}
                          >
                            {savingJob === job.job_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Bookmark className={`w-4 h-4 ${savedJobIds.has(job.job_id) ? 'fill-current' : ''}`} />
                            )}
                          </Button>
                          <Button
                            onClick={() => handleApply(job.job_id)}
                            className="flex-1 sm:flex-none bg-crimson hover:bg-crimson-hover text-white text-xs sm:text-sm uppercase tracking-wider"
                            data-testid={`apply-${job.job_id}`}
                          >
                            Quick Apply
                          </Button>
                        </>
                      )}
                      {/* Share Button - Available to all users */}
                      <div className="relative">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareMenuJob(shareMenuJob === job.job_id ? null : job.job_id);
                          }}
                          variant="outline"
                          className="border-[#333] hover:border-crimson text-gray-400 hover:text-white"
                          data-testid={`share-${job.job_id}`}
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                        {shareMenuJob === job.job_id && (
                          <div className="absolute bottom-full right-0 mb-2 bg-obsidian border border-[#333] p-2 z-50 flex gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); shareJobToTwitter(job); }}
                              className="p-2 hover:bg-[#1DA1F2]/20 transition-colors"
                              title="Twitter"
                            >
                              <Twitter className="w-4 h-4 text-[#1DA1F2]" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); shareJobToLinkedIn(job); }}
                              className="p-2 hover:bg-[#0A66C2]/20 transition-colors"
                              title="LinkedIn"
                            >
                              <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); shareJobToFacebook(job); }}
                              className="p-2 hover:bg-[#1877F2]/20 transition-colors"
                              title="Facebook"
                            >
                              <Facebook className="w-4 h-4 text-[#1877F2]" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); shareJobToWhatsApp(job); }}
                              className="p-2 hover:bg-[#25D366]/20 transition-colors"
                              title="WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4 text-[#25D366]" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); copyJobLink(job.job_id); }}
                              className="p-2 hover:bg-crimson/20 transition-colors"
                              title="Copy Link"
                            >
                              {copiedJobLink === job.job_id ? (
                                <CheckCircle className="w-4 h-4 text-green-400" />
                              ) : (
                                <Link2 className="w-4 h-4 text-crimson" />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-bold mb-2">No Jobs Found</h3>
              <p className="text-gray-400 mb-6">Try adjusting your filters or search terms</p>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="border-[#333] text-white uppercase tracking-wider"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Low Match Warning Modal */}
      {showLowMatchWarning && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold">Low Match Score</h3>
                <p className="text-gray-400 text-sm">
                  Your profile match is {matchScores[showLowMatchWarning]?.score || 0}%
                </p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6">
              Your profile matches less than 50% with this job&apos;s requirements. 
              While you can still apply, consider updating your profile or skills 
              to improve your chances.
            </p>

            {matchScores[showLowMatchWarning]?.details && (
              <div className="mb-6 p-3 bg-obsidian border border-[#1a1a1a]">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Match Breakdown:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Skills:</span>
                    <span className="text-white">{matchScores[showLowMatchWarning].details.skill_score}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Experience:</span>
                    <span className="text-white">{matchScores[showLowMatchWarning].details.experience_score}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Location:</span>
                    <span className="text-white">{matchScores[showLowMatchWarning].details.location_score}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Industry:</span>
                    <span className="text-white">{matchScores[showLowMatchWarning].details.industry_score}%</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setShowLowMatchWarning(null)}
                variant="outline"
                className="flex-1 border-[#333] text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleApply(showLowMatchWarning)}
                className="flex-1 bg-crimson hover:bg-crimson-hover text-white"
              >
                Apply Anyway
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default JobsBrowser;
